import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";

import { useFeedback, useMemoAsync } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { Modal, Tag, Transfer } from "antd";
import { $parse } from "../../state";
import { getObjectTitle } from "../helper";

interface Props {
  object: Parse.Object;
  field: string;
  onClose: () => void;
}

/**
 * TODO: Refactor to table with pagination so items will be fetched on page change
 */
export const RelationDialog = React.memo<Props>(function RelationDialog({
  object,
  field,
  onClose,
}) {
  const t = useTranslation();
  const { message, modal } = useFeedback();
  const [selection, setSelection] = useState<React.Key[]>([]);

  //Right side (selected items)
  const objectClassName = object.className;
  const objectClassSchema = $parse.ui.getClassSchema(objectClassName);
  const targetClassName = objectClassSchema.fields[field].targetClass;
  const targetClassConfig = $parse.ui.getClassConfig(targetClassName);
  const canEdit = $parse.ui.getObjectPermission(object, "update");

  const defaultSelected = useMemoAsync(
    () => object.relation(field).query().limit(10000000).find(),
    [object.id, field, targetClassName],
    []
  );

  useEffect(() => {
    setSelection(defaultSelected.map((obj) => obj.id));
  }, [defaultSelected]);

  const entries = useMemoAsync(
    () => new Parse.Query(targetClassName).limit(9999999).find(),
    [targetClassName],
    []
  );

  const dataSource = useMemo(
    () =>
      entries.map((obj) => ({
        key: obj.id,
        title: getObjectTitle(obj, targetClassConfig),
      })),
    [entries, targetClassConfig]
  );

  const onSave = async () => {
    try {
      if (!object.id) {
        const feedback = await modal.info({
          onOk: () => Promise.resolve(true),
          onCancel: () => Promise.resolve(false),
          okCancel: true,
          title: t("parse-admin:admin.relations.confirm_title"),
          content: t("parse-admin:admin.relations.confirm_content"),
          closable: true,
        });
        if (!feedback) return onClose();
        // If no object ID is present, we need to create a new object
        const savedObject = await object.save();
        object.set("objectId", savedObject.id);
        $parse.ui.currentFieldSetter?.("id", savedObject.id);
      }

      // clear relation queue
      await object.fetch();

      const relToRemove = object.relation(field);
      for (const obj of defaultSelected) {
        if (!selection.includes(obj.id)) {
          relToRemove.remove(obj);
        }
      }

      // clear relation queue
      await object.save();

      const relToAdd = object.relation(field);
      for (const obj of selection) {
        relToAdd.add(
          new Parse.Object(targetClassName, { objectId: obj, id: obj })
        );
      }

      await object.save();

      message.success(
        t("parse-admin:admin.relations.add_success", {
          count: selection.length,
        })
      );

      onClose();
    } catch (error) {
      console.error(error);
      message.error(t("parse-admin:admin.relations.add_error"));
    }
  };

  return (
    <Modal
      open={true}
      title={t("parse-admin:admin.relations.modal_title")}
      okText={t("opendash:ui.save")}
      onOk={canEdit ? onSave : undefined}
      okButtonProps={{ disabled: !canEdit }}
      onCancel={onClose}
      width={800}
    >
      <Transfer
        showSearch
        disabled={!canEdit}
        titles={[
          <Tag>
            {t("parse-admin:admin.relations.leftcount", {
              count: entries.length - selection.length,
            })}
          </Tag>,
          <Tag>
            {t("parse-admin:admin.relations.rightcount", {
              count: selection.length,
            })}
          </Tag>,
        ]}
        dataSource={dataSource}
        targetKeys={selection}
        onChange={setSelection}
        render={(obj) => obj.title}
        rowKey={(obj) => obj.key}
        pagination
        listStyle={{
          width: "100%",
          height: 600,
        }}
      />
    </Modal>
  );
});
